import React, { useState } from 'react';
import { Form, Input, Button, Col, Row } from 'antd';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { LoginFormValues } from '../types/types';
import { setItem } from '../utils/utils';
import { accessTokenKey, COLORS, TEXT_SIZES } from '../utils/constants';
import { BASEPATH } from '../routes/routePaths';
import { sendOtpRequest, verifyOtpRequest } from '../api/requests/login';
import LoginImage from '../assets/loginPage.png';
import { useSnackbar } from '../contexts/SnackbarContext/SnackbarProvider';

const extractOtp = (text: string): string | null => {
  const match = text.match(/\b\d{6}\b/);
  return match ? match[0] : null;
};

const Login: React.FC = () => {
  const snackbar = useSnackbar();
  const [isOtpSent, setIsOtpSent] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleSendOtp = async (values: { mobile: string }) => {
    try {
      setPhoneNumber(values.mobile);
      const response = await sendOtpRequest(values.mobile);

      if (!response) {
        snackbar.error('Failed to send OTP. Please try again later.');
        return;
      }

      if (response?.isFailure) {
        snackbar.error(
          response?.error || 'Failed to send OTP. Please try again later.',
        );
        return;
      }

      if (response?.isSuccess) {
        snackbar.success('OTP sent successfully!');
        const otp = response.value && extractOtp(response.value);
        snackbar.info(`Your OTP is: ${otp}`, '', { duration: 10 });
        setIsOtpSent(true);
      }
    } catch (error: any) {
      snackbar.error(
        error?.Error ||
          'An error occurred while sending OTP. Please try again later.',
      );
    }
  };

  const handleVerifyOtp = async (values: { otp: string }) => {
    const response = await verifyOtpRequest({
      phoneNumber,
      otp: values.otp,
      appType: 0,
    });

    if (!response) {
      snackbar.error('Failed to verify OTP. Please try again later.');
      return;
    }

    if (response?.isSuccess) {
      setItem({ key: accessTokenKey, value: response.value?.token });
      setItem({ key: 'userId', value: response.value?.userId });
      snackbar.success('Login successful!');
      navigate(BASEPATH);
      return;
    }
    snackbar.error(response?.error || 'OTP verification failed.');
    return;
  };

  const handleFinish = async (values: LoginFormValues) => {
    if ('mobile' in values) {
      await handleSendOtp(values);
    } else if ('otp' in values) {
      await handleVerifyOtp(values);
    }
  };

  return (
    <MainContainer>
      <Col span={16}>
        <LoginImageStyles src={LoginImage} alt="login" />
      </Col>
      <FormWrapperStyles span={8}>
        <TitleAndFormWrapper align="middle">
          <StyledLoginContainer span={24}>
            <StyledLoginCard>Login</StyledLoginCard>
            <FormWrapper
              form={form}
              layout="vertical"
              onFinish={(values) => handleFinish(values as LoginFormValues)}
            >
              {!isOtpSent ? (
                <FormItemStyles
                  name="mobile"
                  label="Mobile Number"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your mobile number!',
                    },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: 'Please enter a valid 10-digit mobile number!',
                    },
                  ]}
                >
                  <MobileNumberInput
                    placeholder="Enter your mobile number"
                    maxLength={10}
                  />
                </FormItemStyles>
              ) : (
                <FormItemStyles
                  name="otp"
                  label="Enter OTP"
                  rules={[
                    { required: true, message: 'Please enter the OTP!' },
                    {
                      pattern: /^[0-9]{6}$/,
                      message: 'Please enter a valid 6-digit OTP!',
                    },
                  ]}
                >
                  <Input.OTP length={6} size={'large'} />
                </FormItemStyles>
              )}
              <FormItemStyles>
                <StyledButton type="primary" htmlType="submit" block>
                  {isOtpSent ? 'Verify OTP' : 'Send OTP'}
                </StyledButton>
              </FormItemStyles>
              {isOtpSent && (
                <StyledLinkButton
                  type="link"
                  onClick={() => setIsOtpSent(false)}
                  block
                >
                  Change Mobile Number
                </StyledLinkButton>
              )}
            </FormWrapper>
          </StyledLoginContainer>
        </TitleAndFormWrapper>
      </FormWrapperStyles>
    </MainContainer>
  );
};

const StyledLoginContainer = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 80%;
  gap: 80px;
  background-color: ${COLORS.grey.light};
  padding: 20px;
  border-radius: 10px;
`;

const TitleAndFormWrapper = styled(Row)`
  height: 100%;
`;

const StyledLoginCard = styled.span`
  font-size: ${TEXT_SIZES.SIX_XL}px;
  font-weight: bold;
`;

const StyledButton = styled(Button)`
  margin-top: 16px;
  height: 48px;
  font-size: ${TEXT_SIZES.MD}px;
`;

const StyledLinkButton = styled(Button)`
  margin-top: 8px;
  height: 48px;
`;

const MobileNumberInput = styled(Input)`
  height: 48px;
`;

const FormItemStyles = styled(Form.Item)`
  .ant-form-item-label > label {
    font-size: ${TEXT_SIZES.BASE}px;
  }
`;

const MainContainer = styled(Row)`
  min-height: 100vh;
`;

const LoginImageStyles = styled.img`
  height: 100%;
  width: 100%;
`;

const FormWrapperStyles = styled(Col)`
  padding: 70px;
`;

const FormWrapper = styled(Form)`
  width: 100%;
`;
export default Login;
